"use client";

import { useMemo, useState } from "react";
import { formatAmericanOdds } from "@/libs/vigMath";
import {
  POLYMARKET_CATEGORIES,
  POLYMARKET_CATEGORY_RATES,
  crossVenueArb,
  feeQuote,
  type FeeVenue,
  type PolymarketCategory,
} from "@/libs/predictionFeeMath";

const dollars = (value: number) =>
  `$${value.toFixed(Math.abs(value) < 1 ? 4 : 2)}`;
const cents = (dollarsValue: number, digits = 3) =>
  `${(dollarsValue * 100).toFixed(digits)}¢`;
const percent = (value: number, digits = 2) => `${value.toFixed(digits)}%`;

const inputClass =
  "w-full p-3 rounded-lg bg-dark-7 border border-white/[0.12] focus:border-purple outline-none text-white text-base sm:text-sm";

function Toggle<T extends string>({
  options,
  value,
  onChange,
  labels,
}: {
  options: readonly T[];
  value: T;
  onChange: (next: T) => void;
  labels: Record<string, string>;
}) {
  return (
    <div className="inline-flex flex-wrap rounded-lg border border-white/[0.12] p-1 bg-dark-7">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className={`px-4 py-1.5 rounded-md text-sm transition duration-200 ${
            value === option
              ? "hero-button-gradient text-white font-semibold"
              : "text-white/70 hover:text-white"
          }`}
        >
          {labels[option]}
        </button>
      ))}
    </div>
  );
}

function Result({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-dark-7 border border-white/[0.12] p-3 rounded">
      <div className="text-xs text-white/60">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
    </div>
  );
}

const KalshiPolymarketFeeCalculator = ({ dictionary }) => {
  const t = dictionary.calculators.kalshi_polymarket_fee_calculator;

  const [venue, setVenue] = useState<FeeVenue>("kalshi");
  const [category, setCategory] = useState<PolymarketCategory>("sports");
  const [order, setOrder] = useState<"taker" | "maker">("taker");
  const [price, setPrice] = useState("50");
  const [contracts, setContracts] = useState("100");

  const [arbCategory, setArbCategory] = useState<PolymarketCategory>("sports");
  const [polymarketPrice, setPolymarketPrice] = useState("48");
  const [kalshiPrice, setKalshiPrice] = useState("48");
  const [arbContracts, setArbContracts] = useState("100");

  const quote = useMemo(
    () =>
      feeQuote({
        venue,
        price: parseFloat(price) / 100,
        contracts: parseFloat(contracts),
        maker: order === "maker",
        category,
      }),
    [venue, price, contracts, order, category],
  );

  const arb = useMemo(
    () =>
      crossVenueArb({
        polymarketPrice: parseFloat(polymarketPrice) / 100,
        kalshiPrice: parseFloat(kalshiPrice) / 100,
        contracts: parseFloat(arbContracts),
        category: arbCategory,
      }),
    [polymarketPrice, kalshiPrice, arbContracts, arbCategory],
  );

  const categoryOptions = (
    <>
      {POLYMARKET_CATEGORIES.map((option) => (
        <option key={option} value={option}>
          {t.categories[option]} ({POLYMARKET_CATEGORY_RATES[option]})
        </option>
      ))}
    </>
  );

  return (
    <section className="pb-17.5 lg:pb-22.5 xl:pb-27.5">
      <div className="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 lg:col-start-3 space-y-8">
          <div className="p-6 sm:p-8 bg-dark-8 gradient-box rounded-lg text-white">
            <h2 className="text-xl font-semibold mb-5">{t.trade_heading}</h2>

            <div className="mb-4">
              <span className="block mb-2">{t.venue}</span>
              <Toggle<FeeVenue>
                options={["kalshi", "polymarket"] as const}
                value={venue}
                onChange={setVenue}
                labels={t}
              />
            </div>

            {venue === "polymarket" && (
              <div className="mb-4">
                <label className="block mb-2" htmlFor="fee-category">
                  {t.category}
                </label>
                <select
                  id="fee-category"
                  value={category}
                  onChange={(event) =>
                    setCategory(event.target.value as PolymarketCategory)
                  }
                  className={inputClass}
                >
                  {categoryOptions}
                </select>
              </div>
            )}

            <div className="mb-4">
              <span className="block mb-2">{t.order_type}</span>
              <Toggle<"taker" | "maker">
                options={["taker", "maker"] as const}
                value={order}
                onChange={setOrder}
                labels={t}
              />
              {order === "maker" && (
                <p className="mt-2 text-sm text-white/70">
                  {venue === "polymarket"
                    ? t.maker_note_polymarket
                    : t.maker_note_kalshi}
                </p>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-2">
              <div>
                <label className="block mb-1" htmlFor="fee-price">
                  {t.price}
                </label>
                <input
                  id="fee-price"
                  type="number"
                  inputMode="decimal"
                  min={0.1}
                  max={99.9}
                  step={0.1}
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block mb-1" htmlFor="fee-contracts">
                  {venue === "polymarket" ? t.shares : t.contracts}
                </label>
                <input
                  id="fee-contracts"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  value={contracts}
                  onChange={(event) => setContracts(event.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            {quote ? (
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2">
                <Result label={t.fee} value={dollars(quote.fee)} />
                <Result label={t.fee_per_contract} value={cents(quote.feePerContract)} />
                <Result label={t.fee_percent} value={percent(quote.feePercent)} />
                <Result label={t.total_cost} value={dollars(quote.totalCost)} />
                <Result label={t.break_even} value={percent(quote.breakEven * 100)} />
                <Result
                  label={t.net_odds}
                  value={`${quote.netOdds.toFixed(3)} / ${formatAmericanOdds(quote.netOdds)}`}
                />
                <Result label={t.payout} value={dollars(quote.payout)} />
                <Result label={t.profit_if_win} value={dollars(quote.profitIfWin)} />
              </div>
            ) : (
              <p className="text-red-500 mt-3">{t.input_error}</p>
            )}
          </div>

          <div className="p-6 sm:p-8 bg-dark-8 gradient-box rounded-lg text-white">
            <h2 className="text-xl font-semibold mb-2">{t.arb_heading}</h2>
            <p className="mb-5 text-sm text-white/70">{t.arb_intro}</p>

            <div className="mb-4">
              <label className="block mb-2" htmlFor="arb-category">
                {t.category}
              </label>
              <select
                id="arb-category"
                value={arbCategory}
                onChange={(event) =>
                  setArbCategory(event.target.value as PolymarketCategory)
                }
                className={inputClass}
              >
                {categoryOptions}
              </select>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-2">
              <div>
                <label className="block mb-1" htmlFor="arb-polymarket">
                  {t.polymarket_price}
                </label>
                <input
                  id="arb-polymarket"
                  type="number"
                  inputMode="decimal"
                  min={0.1}
                  max={99.9}
                  step={0.1}
                  value={polymarketPrice}
                  onChange={(event) => setPolymarketPrice(event.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block mb-1" htmlFor="arb-kalshi">
                  {t.kalshi_price}
                </label>
                <input
                  id="arb-kalshi"
                  type="number"
                  inputMode="decimal"
                  min={0.1}
                  max={99.9}
                  step={0.1}
                  value={kalshiPrice}
                  onChange={(event) => setKalshiPrice(event.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block mb-1" htmlFor="arb-contracts">
                  {t.contracts}
                </label>
                <input
                  id="arb-contracts"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  value={arbContracts}
                  onChange={(event) => setArbContracts(event.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            {arb ? (
              <>
                <p
                  className={`mt-5 mb-3 text-lg font-semibold ${
                    arb.isArb ? "text-green-400" : "text-red-400"
                  }`}
                >
                  {arb.isArb ? t.verdict_arb : t.verdict_no_arb}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Result label={t.polymarket_fee} value={dollars(arb.polymarket.fee)} />
                  <Result label={t.kalshi_fee} value={dollars(arb.kalshi.fee)} />
                  <Result
                    label={t.cost_per_dollar}
                    value={cents(arb.totalCost / arb.payout, 2)}
                  />
                  <Result label={t.total_cost} value={dollars(arb.totalCost)} />
                  <Result label={t.arb_profit} value={dollars(arb.profit)} />
                  <Result label={t.arb_return} value={percent(arb.returnPercent)} />
                </div>
              </>
            ) : (
              <p className="text-red-500 mt-3">{t.input_error}</p>
            )}
          </div>

          <p className="text-sm text-white/60">{t.source_note}</p>
        </div>
      </div>
    </section>
  );
};

export default KalshiPolymarketFeeCalculator;
